// React
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Button, Divider } from "@mui/material";
// Others
import { useSnackbar } from "notistack";
import EmployeeInfo from "../component/EmployeeInfo";

import { updateEmployee } from "../model";
import {
  filterDifferences,
} from "../../../merchants/offline-merchant-detail/utils";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import { stripLoginPrefix } from "../utils/stripLoginPrefix";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const EMPLOYEE_LOCALE_KEY = "page.employees.";

const EmployeeDetail = ({ employeeDetail, redirectFn, fetchEmployeeDetail }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const onSubmit = async (formValue) => {
    try {
      const employeeFields = Object.keys(
        t("page.employees.employeeDetail.detail.fields", {
          returnObjects: true,
        }),
      );
      setIsLoading(true);

      // Because firstname and lastname in DB is one word.
      const payload = filterDifferences(employeeDetail, formValue, [
        ...employeeFields,
        "firstname",
        "lastname",
        "isAutoSettlement",
      ]);
      if (
        Object.keys(payload).includes("settlementDate") &&
        payload.settlementDate !== ""
      )
        payload.isAutoSettlement = true;
      if (Object.keys(payload).includes("email")) {
        payload.email = stripLoginPrefix(payload.email, employeeDetail?.payfacPrefix);
      }

      console.log("Employee detail update payload", payload);
      if (Object.keys(payload).length === 0) {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.noChanges"),
          "info",
        );
        return;
      }
      const updateEmployeeResp = await updateEmployee({
        employeeId: employeeDetail.employeeId,
        merchantId: employeeDetail.merchantId,
        email: stripLoginPrefix(employeeDetail.email, employeeDetail?.payfacPrefix),
        ...payload,
      });
      if (updateEmployeeResp?.data?.responseCode === "0000") {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateSuccess"),
          "success",
        );
      } else {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed"),
          "error",
        );
      }
      await fetchEmployeeDetail(employeeDetail.employeeId);
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsEditing(false);
      setIsLoading(false);
      clearFormMemory();
    }
  };

  const buttons = () => (
    <Stack direction="row" justifyContent="flex-end" gap={2} flexWrap="wrap">
      {!isEditing ? (
        <Button variant="normal-button" onClick={() => {
          setIsEditing(true);
          rememberForm()
        }
        }>
          {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.edit`)}
        </Button>
      ) : (
        <>
          <Button
            variant="outline-normal-button"
            onClick={() => {
              setIsEditing(false);
              undoForm();
            }}
            type="button"
            disabled={isLoading}
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.cancel`)}
          </Button>
          <ButtonMAS
            overlayLoading
            isLoading={isLoading}
            disabled={isLoading}
            type="submit"
          >
            {t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.button.save`)}
          </ButtonMAS>
        </>
      )}
    </Stack>
  );

  const setAllValues = (initialValue) => {
    reset({
      ...initialValue,
      permission: JSON.parse(initialValue?.permission ?? "{}")
        ?.allowedComponent,
    });
  };



  useEffect(() => {
    if (employeeDetail) {
      setAllValues(employeeDetail);
    }
  }, [employeeDetail]);

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
        <EmployeeInfo
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
          redirectFn={redirectFn}
          preferredLoginPrefix={employeeDetail?.payfacPrefix || ""}
        />
        <Divider style={{ margin: "24px 0" }} />
        {/* <EmployeeSettlement
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
          employee={employeeDetail}
          isOtherProcessor={isOtherProcessor}
        /> */}
        {buttons()}
      </form>
      {/* <Divider style={{ margin: "24px 0" }} /> */}
      {/* <EmployeeProcessorAccInfo
        employee={employeeDetail}
        isLoading={isLoading}
        onEditRow={onEditRow}
      /> */}
    </Stack>
  );
};

export default React.memo(EmployeeDetail);
